/**
 * 🎨 Q&A Floating Toolbar - Q&A Element Styling
 * 
 * Floating Toolbar für Q&A Container & Items
 * Ermöglicht Inline-Editing von Q&A Properties
 * 
 * Features:
 * - Container Styling: Background, Border, Shadow, Padding, Gap
 * - Item Styling: Background, Border, Shadow, Border Radius
 * - Icon Settings: Style & Color
 * 
 * ✅ Matched to ButtonFloatingToolbar Design:
 * - Drag & Drop functionality
 * - Icon-based sections
 * - Pill-shaped → rounded transition
 * - Desktop/Mobile toggles in sections
 * - No title header
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './QAFloatingToolbar.css';

function QAFloatingToolbar({
    // Required Props
    isEditMode,
    isVisible,
    
    // Container Styling
    backgroundColor,
    borderEnabled,
    borderWidth,
    borderColor,
    borderRadius,
    borderRadiusMobile,
    shadowEnabled,
    shadowIntensity,
    shadowDistance,
    shadowOpacity,
    padding,
    paddingMobile,
    gap,
    textAlign,
    textAlignMobile,
    
    // Item Styling
    itemBackgroundColor,
    itemBorderEnabled,
    itemBorderWidth,
    itemBorderColor,
    itemBorderRadius,
    itemBorderRadiusMobile,
    itemShadowEnabled,
    itemShadowIntensity,
    itemShadowDistance,
    itemShadowOpacity,
    itemPadding,
    itemPaddingMobile,
    separatorLineMode,
    separatorLineWidth,
    separatorLineColor,
    
    // Icon Settings
    iconStyle,
    iconColor,
    
    // Callbacks
    onStyleChange, // (styles) => void
    colors = {}, // Brand Colors
    onClose, // () => void
    
    // Additional Props
    containerRef, // Ref zum Container
    isMobileLayout = false
}) {
    // 🎨 Toolbar State
    const [showToolbar, setShowToolbar] = useState(false);
    const [toolbarPosition, setToolbarPosition] = useState({ top: 80, left: window.innerWidth / 2 });
    const toolbarRef = useRef(null);
    
    // 🎯 Expandable Sections State
    const [expandedSection, setExpandedSection] = useState(null); 
    // 'container-bg', 'container-border', 'item-styling', 'icon-settings'
    
    // 🎯 Device Toggle
    const [device, setDevice] = useState(isMobileLayout ? 'mobile' : 'desktop');
    
    // 🎯 Dragging State
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isManuallyPositioned, setIsManuallyPositioned] = useState(false);
    
    // 🎨 Custom Color Slots (shared with TipTap)
    const [customColorSlots, setCustomColorSlots] = useState(() => {
        try {
            const stored = localStorage.getItem('tiptap-custom-color-slots');
            return stored ? JSON.parse(stored) : ['#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff'];
        } catch (error) {
            return ['#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff'];
        }
    });
    
    const [editingSlotIndex, setEditingSlotIndex] = useState(null);
    
    // Sync Custom Color Slots
    useEffect(() => {
        try {
            localStorage.setItem('tiptap-custom-color-slots', JSON.stringify(customColorSlots));
        } catch (error) {
            console.error('Failed to save custom color slots:', error);
        }
    }, [customColorSlots]);
    
    useEffect(() => {
        if (expandedSection === 'container-bg' || expandedSection === 'item-styling') {
            try {
                const stored = localStorage.getItem('tiptap-custom-color-slots');
                if (stored) {
                    const storedSlots = JSON.parse(stored);
                    if (JSON.stringify(storedSlots) !== JSON.stringify(customColorSlots)) {
                        setCustomColorSlots(storedSlots);
                    }
                }
            } catch (error) {
                console.error('Failed to sync custom color slots:', error);
            }
        }
    }, [expandedSection]);
    
    const updateColorSlot = (slotIndex, newColor) => {
        const newSlots = [...customColorSlots];
        newSlots[slotIndex] = newColor;
        setCustomColorSlots(newSlots);
    };
    
    // 🎯 Drag Handlers
    const handleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - toolbarPosition.left,
            y: e.clientY - toolbarPosition.top
        });
    };
    
    const handleMouseMove = (e) => {
        if (!isDragging) return;
        
        const newLeft = e.clientX - dragOffset.x;
        const newTop = e.clientY - dragOffset.y;
        
        // Bounds checking
        const maxLeft = window.innerWidth - (toolbarRef.current?.offsetWidth || 400);
        const maxTop = window.innerHeight - (toolbarRef.current?.offsetHeight || 300);
        
        setToolbarPosition({
            left: Math.max(0, Math.min(newLeft, maxLeft)),
            top: Math.max(0, Math.min(newTop, maxTop))
        });
    };
    
    const handleMouseUp = () => {
        setIsDragging(false);
        setIsManuallyPositioned(true);
    };
    
    // ✅ Drag Event Listeners
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDragging]);
    
    // Sync Device Toggle
    useEffect(() => {
        setDevice(isMobileLayout ? 'mobile' : 'desktop');
    }, [isMobileLayout]);

    // Sync Toolbar Visibility
    useEffect(() => {
        if (isEditMode && isVisible) {
            setShowToolbar(true);
        } else {
            setShowToolbar(false);
            setExpandedSection(null);
        }
    }, [isEditMode, isVisible]);

    // Resize Handler
    useEffect(() => {
        if (!showToolbar || isManuallyPositioned) return;
        
        const handleResize = () => {
            if (!expandedSection) {
                const left = window.innerWidth / 2;
                setToolbarPosition(prev => ({ ...prev, left }));
            }
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [showToolbar, expandedSection, isManuallyPositioned]);

    // Outside Click Handler
    useEffect(() => {
        if (!showToolbar || !isVisible) return;
        
        const handleClickOutside = (event) => {
            const clickedInToolbar = toolbarRef.current && toolbarRef.current.contains(event.target);
            const clickedInColorPicker = event.target.closest('input[type="color"]');
            
            if (!clickedInToolbar && !clickedInColorPicker) {
                setExpandedSection(null);
                if (onClose) onClose();
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showToolbar, isVisible, onClose]);

    if (!isEditMode || !isVisible || !showToolbar) {
        return null;
    }

    // Active Values (responsive)
    const activeBorderRadius = device === 'mobile' 
        ? (borderRadiusMobile ?? borderRadius ?? 8)
        : (borderRadius ?? 8);
    
    const activePadding = device === 'mobile'
        ? (paddingMobile ?? padding ?? 16)
        : (padding ?? 16);
    
    const activeItemBorderRadius = device === 'mobile'
        ? (itemBorderRadiusMobile ?? itemBorderRadius ?? 6)
        : (itemBorderRadius ?? 6);
    
    const activeItemPadding = device === 'mobile'
        ? (itemPaddingMobile ?? itemPadding ?? 16)
        : (itemPadding ?? 16);

    return ReactDOM.createPortal(
        <div 
            ref={toolbarRef}
            className={`qa-floating-toolbar-portal ${expandedSection ? 'expanded' : ''} ${isDragging ? 'dragging' : ''}`}
            style={{
                position: 'fixed',
                top: `${toolbarPosition.top}px`,
                left: `${toolbarPosition.left}px`,
                transform: 'translateX(-50%)',
                zIndex: 999999
            }}
        >
            <div 
                className="qa-toolbar"
                onMouseDown={(e) => {
                    // ✅ Stoppe mousedown Events innerhalb der Toolbar
                    e.stopPropagation();
                }}
            >
                {/* 🎨 MAIN TOOLBAR BUTTONS - Icon Row */}
                <div className="qa-toolbar-row">
                    {/* 🎯 Drag Handle Icon */}
                    <div 
                        className="qa-drag-handle"
                        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                        title="Drag to move toolbar"
                        onMouseDown={handleMouseDown}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <i className="fas fa-grip-vertical"></i>
                    </div>
                    
                    {/* Add Q&A Button */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (onStyleChange) {
                                onStyleChange({ addNewQA: true });
                            }
                        }}
                        className="qa-toolbar-btn qa-add-toolbar-btn"
                        title="Add Q&A Item"
                    >
                        <i className="fas fa-plus"></i>
                    </button>
                    
                    <div className="qa-toolbar-separator" />
                    
                    {/* 1. Container Background */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'container-bg' ? null : 'container-bg');
                        }}
                        className={`qa-toolbar-btn ${expandedSection === 'container-bg' ? 'is-active' : ''}`}
                        title="Container Background"
                    >
                        <i className="fas fa-fill-drip"></i>
                    </button>

                    {/* 2. Container Border & Shadow */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'container-border' ? null : 'container-border');
                        }}
                        className={`qa-toolbar-btn ${expandedSection === 'container-border' ? 'is-active' : ''} ${(borderEnabled || shadowEnabled) ? 'has-feature' : ''}`}
                        title="Container Settings"
                    >
                        <i className="fas fa-border-style"></i>
                    </button>

                    {/* 3. Item Styling */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'item-styling' ? null : 'item-styling');
                        }}
                        className={`qa-toolbar-btn ${expandedSection === 'item-styling' ? 'is-active' : ''}`}
                        title="Q&A Item Styling"
                    >
                        <i className="fas fa-rectangle-list"></i>
                    </button>

                    {/* 4. Icon Settings */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'icon-settings' ? null : 'icon-settings');
                        }}
                        className={`qa-toolbar-btn ${expandedSection === 'icon-settings' ? 'is-active' : ''}`}
                        title="Icon Settings"
                    >
                        <i className="fas fa-icons"></i>
                    </button>
                </div>
                
                {/* 🎨 EXPANDABLE SECTIONS */}
                
                {/* 1️⃣ CONTAINER BACKGROUND */}
                {expandedSection === 'container-bg' && (
                    <div className="qa-expanded-section">
                        <div className="qa-color-group">
                            <span className="qa-color-label">Background Color</span>
                            
                            {/* Brand Colors */}
                            <div className="qa-brand-colors">
                                {Object.values(colors).map((brandColor, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className="qa-brand-color"
                                        style={{ backgroundColor: brandColor }}
                                        onClick={() => {
                                            onStyleChange({ qaBackgroundColor: brandColor });
                                        }}
                                        title={`Brand Color ${index + 1}`}
                                    />
                                ))}
                            </div>
                            
                            {/* Custom Color Slots */}
                            <div className="qa-custom-color-slots">
                                {customColorSlots.map((slotColor, slotIndex) => (
                                    <React.Fragment key={slotIndex}>
                                        <button
                                            type="button"
                                            className="qa-brand-color qa-color-slot"
                                            style={{ 
                                                backgroundColor: slotColor,
                                                border: slotColor === '#ffffff' ? '2px dashed rgba(255, 255, 255, 0.3)' : '2px solid rgba(255, 255, 255, 0.3)'
                                            }}
                                            onClick={() => {
                                                if (slotColor !== '#ffffff') {
                                                    onStyleChange({ qaBackgroundColor: slotColor });
                                                } else {
                                                    setEditingSlotIndex(slotIndex);
                                                    document.getElementById(`qa-color-slot-${slotIndex}`).click();
                                                }
                                            }}
                                            onContextMenu={(e) => {
                                                e.preventDefault();
                                                setEditingSlotIndex(slotIndex);
                                                document.getElementById(`qa-color-slot-${slotIndex}`).click();
                                            }}
                                            title={slotColor === '#ffffff' ? 'Click to set custom color' : `Custom Color: ${slotColor}`}
                                        >
                                            {slotColor === '#ffffff' && (
                                                <i className="fas fa-plus" style={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.5)' }}></i>
                                            )}
                                        </button>
                                        
                                        <input
                                            id={`qa-color-slot-${slotIndex}`}
                                            type="color"
                                            value={slotColor}
                                            style={{ display: 'none' }}
                                            onChange={(e) => updateColorSlot(slotIndex, e.target.value)}
                                        />
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                        
                        <span className="qa-section-label">Container Background</span>
                    </div>
                )}

                {/* 2️⃣ CONTAINER BORDER & SHADOW */}
                {expandedSection === 'container-border' && (
                    <div className="qa-expanded-section qa-effects-section">
                        {/* Desktop/Mobile Toggle */}
                        <div className="floating-typo-device-row">
                            <span className="floating-typo-device-label">Desktop</span>
                            <div 
                                className={`floating-typo-toggle ${device === 'mobile' ? 'mobile' : ''}`}
                                onClick={() => setDevice(device === 'desktop' ? 'mobile' : 'desktop')}
                            >
                                <div className="floating-typo-toggle-slider"></div>
                            </div>
                            <span className="floating-typo-device-label">Mobile</span>
                        </div>
                        
                        {/* Padding Top/Bottom/Left/Right */}
                        <div className="typo-control">
                            <label>Padding</label>
                            <input
                                type="range"
                                min="0"
                                max="80"
                                value={activePadding}
                                onChange={(e) => {
                                    const newValue = parseInt(e.target.value);
                                    if (device === 'desktop') {
                                        onStyleChange({ qaPadding: newValue });
                                    } else {
                                        onStyleChange({ qaPaddingMobile: newValue });
                                    }
                                }}
                                className="typo-slider"
                            />
                            <span className="typo-val">{activePadding}px</span>
                        </div>
                        
                        {/* Gap */}
                        <div className="typo-control">
                            <label>Gap</label>
                            <input
                                type="range"
                                min="0"
                                max="40"
                                value={gap ?? 12}
                                onChange={(e) => {
                                    onStyleChange({ qaGap: parseInt(e.target.value) });
                                }}
                                className="typo-slider"
                            />
                            <span className="typo-val">{gap ?? 12}px</span>
                        </div>
                        
                        {/* Text Alignment */}
                        <div className="typo-control">
                            <label>Align</label>
                            <div className="qa-align-buttons">
                                <button
                                    className={`qa-align-btn ${(device === 'desktop' ? (textAlign || 'left') : (textAlignMobile || 'left')) === 'left' ? 'active' : ''}`}
                                    onClick={() => {
                                        if (device === 'desktop') {
                                            onStyleChange({ qaTextAlign: 'left' });
                                        } else {
                                            onStyleChange({ qaTextAlignMobile: 'left' });
                                        }
                                    }}
                                    title="Align Left"
                                >
                                    <i className="fas fa-align-left"></i>
                                </button>
                                <button
                                    className={`qa-align-btn ${(device === 'desktop' ? (textAlign || 'left') : (textAlignMobile || 'left')) === 'right' ? 'active' : ''}`}
                                    onClick={() => {
                                        if (device === 'desktop') {
                                            onStyleChange({ qaTextAlign: 'right' });
                                        } else {
                                            onStyleChange({ qaTextAlignMobile: 'right' });
                                        }
                                    }}
                                    title="Align Right"
                                >
                                    <i className="fas fa-align-right"></i>
                                </button>
                            </div>
                        </div>
                        
                        {/* Border Radius */}
                        <div className="typo-control">
                            <label>Radius</label>
                            <input
                                type="range"
                                min="0"
                                max="40"
                                value={activeBorderRadius}
                                onChange={(e) => {
                                    const newValue = parseInt(e.target.value);
                                    if (device === 'desktop') {
                                        onStyleChange({ qaBorderRadius: newValue });
                                    } else {
                                        onStyleChange({ qaBorderRadiusMobile: newValue });
                                    }
                                }}
                                className="typo-slider"
                            />
                            <span className="typo-val">{activeBorderRadius}px</span>
                        </div>
                        
                        <div className="qa-effect-divider" />
                        
                        {/* Border Toggle */}
                        <div className="qa-effect-group">
                            <div className="qa-effect-header">
                                <label className="btn-custom-toggle-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={borderEnabled || false}
                                        onChange={(e) => onStyleChange({ qaBorderEnabled: e.target.checked })}
                                        className="btn-custom-toggle-input"
                                    />
                                    <span className="btn-custom-toggle-slider"></span>
                                    <span className="btn-custom-toggle-label">Border</span>
                                </label>
                            </div>
                            
                            {borderEnabled && (
                                <div className="qa-effect-controls">
                                    <div className="typo-control">
                                        <label>Width</label>
                                        <input
                                            type="range"
                                            min="1"
                                            max="8"
                                            value={borderWidth || 1}
                                            onChange={(e) => {
                                                onStyleChange({ qaBorderWidth: parseInt(e.target.value) });
                                            }}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">{borderWidth || 1}px</span>
                                    </div>
                                    
                                    {/* Border Color */}
                                    <div className="qa-brand-colors qa-brand-colors-small">
                                        {Object.values(colors).map((brandColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className="qa-brand-color qa-brand-color-small"
                                                style={{ backgroundColor: brandColor }}
                                                onClick={() => {
                                                    onStyleChange({ qaBorderColor: brandColor });
                                                }}
                                                title={`Border Color ${index + 1}`}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="qa-effect-divider" />
                        
                        {/* Shadow Toggle */}
                        <div className="qa-effect-group">
                            <div className="qa-effect-header">
                                <label className="btn-custom-toggle-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={shadowEnabled || false}
                                        onChange={(e) => onStyleChange({ qaShadowEnabled: e.target.checked })}
                                        className="btn-custom-toggle-input"
                                    />
                                    <span className="btn-custom-toggle-slider"></span>
                                    <span className="btn-custom-toggle-label">Shadow</span>
                                </label>
                            </div>
                            
                            {shadowEnabled && (
                                <div className="qa-effect-controls">
                                    <div className="typo-control">
                                        <label>Blur</label>
                                        <input
                                            type="range"
                                            min="5"
                                            max="50"
                                            value={shadowIntensity || 15}
                                            onChange={(e) => {
                                                onStyleChange({ qaShadowIntensity: parseInt(e.target.value) });
                                            }}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">{shadowIntensity || 15}px</span>
                                    </div>
                                    
                                    <div className="typo-control">
                                        <label>Distance</label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="30"
                                            value={shadowDistance || 4}
                                            onChange={(e) => {
                                                onStyleChange({ qaShadowDistance: parseInt(e.target.value) });
                                            }}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">{shadowDistance || 4}px</span>
                                    </div>
                                    
                                    <div className="typo-control">
                                        <label>Opacity</label>
                                        <input
                                            type="range"
                                            min="5"
                                            max="80"
                                            value={shadowOpacity || 15}
                                            onChange={(e) => {
                                                onStyleChange({ qaShadowOpacity: parseInt(e.target.value) });
                                            }}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">{shadowOpacity || 15}%</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <span className="qa-section-label">Container Border & Shadow</span>
                    </div>
                )}

                {/* 3️⃣ ITEM STYLING */}
                {expandedSection === 'item-styling' && (
                    <div className="qa-expanded-section">
                        {/* Presets */}
                        <div className="qa-presets-section">
                            <span className="qa-color-label">Style Presets</span>
                            <div className="qa-preset-buttons">
                                {/* Minimal */}
                                <button
                                    className="qa-preset-btn"
                                    onClick={() => {
                                        onStyleChange({
                                            qaItemBackgroundColor: 'transparent',
                                            qaItemBorderEnabled: true,
                                            qaItemBorderWidth: 1,
                                            qaItemBorderColor: '#e0e0e0',
                                            qaItemBorderRadius: 0,
                                            qaItemBorderRadiusMobile: 0,
                                            qaItemShadowEnabled: false
                                        });
                                    }}
                                    title="Minimal Style"
                                >
                                    <i className="fas fa-minus"></i>
                                    <span>Minimal</span>
                                </button>
                                
                                {/* Card */}
                                <button
                                    className="qa-preset-btn"
                                    onClick={() => {
                                        onStyleChange({
                                            qaItemBackgroundColor: '#ffffff',
                                            qaItemBorderEnabled: true,
                                            qaItemBorderWidth: 1,
                                            qaItemBorderColor: '#e0e0e0',
                                            qaItemBorderRadius: 8,
                                            qaItemBorderRadiusMobile: 6,
                                            qaItemShadowEnabled: true,
                                            qaItemShadowIntensity: 10,
                                            qaItemShadowDistance: 2,
                                            qaItemShadowOpacity: 10
                                        });
                                    }}
                                    title="Card Style"
                                >
                                    <i className="fas fa-square"></i>
                                    <span>Card</span>
                                </button>
                                
                                {/* Soft */}
                                <button
                                    className="qa-preset-btn"
                                    onClick={() => {
                                        onStyleChange({
                                            qaItemBackgroundColor: '#f8f9fa',
                                            qaItemBorderEnabled: false,
                                            qaItemBorderRadius: 12,
                                            qaItemBorderRadiusMobile: 10,
                                            qaItemShadowEnabled: false
                                        });
                                    }}
                                    title="Soft Style"
                                >
                                    <i className="fas fa-circle"></i>
                                    <span>Soft</span>
                                </button>
                                
                                {/* Bold */}
                                <button
                                    className="qa-preset-btn"
                                    onClick={() => {
                                        onStyleChange({
                                            qaItemBackgroundColor: '#ffffff',
                                            qaItemBorderEnabled: true,
                                            qaItemBorderWidth: 2,
                                            qaItemBorderColor: colors.accentColor || '#5C80BC',
                                            qaItemBorderRadius: 10,
                                            qaItemBorderRadiusMobile: 8,
                                            qaItemShadowEnabled: true,
                                            qaItemShadowIntensity: 20,
                                            qaItemShadowDistance: 4,
                                            qaItemShadowOpacity: 20
                                        });
                                    }}
                                    title="Bold Style"
                                >
                                    <i className="fas fa-certificate"></i>
                                    <span>Bold</span>
                                </button>
                            </div>
                        </div>
                        
                        <div className="qa-effect-divider" />
                        
                        {/* Desktop/Mobile Toggle */}
                        <div className="floating-typo-device-row">
                            <span className="floating-typo-device-label">Desktop</span>
                            <div 
                                className={`floating-typo-toggle ${device === 'mobile' ? 'mobile' : ''}`}
                                onClick={() => setDevice(device === 'desktop' ? 'mobile' : 'desktop')}
                            >
                                <div className="floating-typo-toggle-slider"></div>
                            </div>
                            <span className="floating-typo-device-label">Mobile</span>
                        </div>
                        
                        {/* Item Background Color */}
                        <div className="qa-color-group">
                            <span className="qa-color-label">Item Background</span>
                            
                            {/* Brand Colors */}
                            <div className="qa-brand-colors qa-brand-colors-small">
                                {Object.values(colors).map((brandColor, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className="qa-brand-color qa-brand-color-small"
                                        style={{ backgroundColor: brandColor }}
                                        onClick={() => {
                                            onStyleChange({ qaItemBackgroundColor: brandColor });
                                        }}
                                        title={`Item Background ${index + 1}`}
                                    />
                                ))}
                            </div>
                            
                            {/* Common Colors */}
                            <div className="qa-brand-colors qa-brand-colors-small" style={{ marginTop: '4px' }}>
                                {['#ffffff', '#f8f9fa', '#e9ecef', 'transparent'].map((commonColor, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className="qa-brand-color qa-brand-color-small"
                                        style={{ 
                                            backgroundColor: commonColor === 'transparent' ? '#ffffff' : commonColor,
                                            border: commonColor === 'transparent' ? '1px dashed rgba(255,255,255,0.5)' : '2px solid rgba(255, 255, 255, 0.3)',
                                            position: 'relative'
                                        }}
                                        onClick={() => {
                                            onStyleChange({ qaItemBackgroundColor: commonColor });
                                        }}
                                        title={commonColor === 'transparent' ? 'Transparent' : commonColor}
                                    >
                                        {commonColor === 'transparent' && (
                                            <i className="fas fa-slash" style={{ fontSize: '8px', color: 'rgba(255,255,255,0.6)' }}></i>
                                        )}
                                    </button>
                                ))}
                            </div>
                            
                            {/* Custom Color Slots */}
                            <div className="qa-custom-color-slots" style={{ marginTop: '4px' }}>
                                {customColorSlots.slice(0, 3).map((slotColor, slotIndex) => (
                                    <React.Fragment key={slotIndex}>
                                        <button
                                            type="button"
                                            className="qa-brand-color qa-color-slot qa-brand-color-small"
                                            style={{ 
                                                backgroundColor: slotColor,
                                                border: slotColor === '#ffffff' ? '2px dashed rgba(255, 255, 255, 0.3)' : '2px solid rgba(255, 255, 255, 0.3)'
                                            }}
                                            onClick={() => {
                                                if (slotColor !== '#ffffff') {
                                                    onStyleChange({ qaItemBackgroundColor: slotColor });
                                                } else {
                                                    setEditingSlotIndex(slotIndex);
                                                    document.getElementById(`qa-item-color-slot-${slotIndex}`).click();
                                                }
                                            }}
                                            onContextMenu={(e) => {
                                                e.preventDefault();
                                                setEditingSlotIndex(slotIndex);
                                                document.getElementById(`qa-item-color-slot-${slotIndex}`).click();
                                            }}
                                            title={slotColor === '#ffffff' ? 'Click to set custom color' : `Custom: ${slotColor}`}
                                        >
                                            {slotColor === '#ffffff' && (
                                                <i className="fas fa-plus" style={{ fontSize: '8px', color: 'rgba(255, 255, 255, 0.5)' }}></i>
                                            )}
                                        </button>
                                        
                                        <input
                                            id={`qa-item-color-slot-${slotIndex}`}
                                            type="color"
                                            value={slotColor}
                                            style={{ display: 'none' }}
                                            onChange={(e) => updateColorSlot(slotIndex, e.target.value)}
                                        />
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                        
                        {/* Item Padding */}
                        <div className="typo-control">
                            <label>Padding</label>
                            <input
                                type="range"
                                min="0"
                                max="40"
                                value={activeItemPadding}
                                onChange={(e) => {
                                    const newValue = parseInt(e.target.value);
                                    if (device === 'desktop') {
                                        onStyleChange({ qaItemPadding: newValue });
                                    } else {
                                        onStyleChange({ qaItemPaddingMobile: newValue });
                                    }
                                }}
                                className="typo-slider"
                            />
                            <span className="typo-val">{activeItemPadding}px</span>
                        </div>
                        
                        {/* Item Border Radius */}
                        <div className="typo-control">
                            <label>Radius</label>
                            <input
                                type="range"
                                min="0"
                                max="30"
                                value={activeItemBorderRadius}
                                onChange={(e) => {
                                    const newValue = parseInt(e.target.value);
                                    if (device === 'desktop') {
                                        onStyleChange({ qaItemBorderRadius: newValue });
                                    } else {
                                        onStyleChange({ qaItemBorderRadiusMobile: newValue });
                                    }
                                }}
                                className="typo-slider"
                            />
                            <span className="typo-val">{activeItemBorderRadius}px</span>
                        </div>
                        
                        <div className="qa-effect-divider" />
                        
                        {/* Separator Line Mode */}
                        <div className="qa-effect-group">
                            <div className="qa-effect-header">
                                <label className="btn-custom-toggle-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={separatorLineMode || false}
                                        onChange={(e) => onStyleChange({ qaSeparatorLineMode: e.target.checked })}
                                        className="btn-custom-toggle-input"
                                    />
                                    <span className="btn-custom-toggle-slider"></span>
                                    <span className="btn-custom-toggle-label">Separator</span>
                                </label>
                            </div>
                            
                            {separatorLineMode && (
                                <div className="qa-effect-controls">
                                    <div className="typo-control">
                                        <label>Width</label>
                                        <input
                                            type="range"
                                            min="1"
                                            max="8"
                                            value={separatorLineWidth || 1}
                                            onChange={(e) => {
                                                onStyleChange({ qaSeparatorLineWidth: parseInt(e.target.value) });
                                            }}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">{separatorLineWidth || 1}px</span>
                                    </div>
                                    
                                    <span className="qa-color-label" style={{ fontSize: '10px', marginBottom: '4px' }}>Line Color</span>
                                    
                                    {/* Brand Colors für Line */}
                                    <div className="qa-brand-colors qa-brand-colors-small">
                                        {Object.values(colors).map((brandColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className="qa-brand-color qa-brand-color-small"
                                                style={{ backgroundColor: brandColor }}
                                                onClick={() => {
                                                    onStyleChange({ qaSeparatorLineColor: brandColor });
                                                }}
                                                title={`Line Color ${index + 1}`}
                                            />
                                        ))}
                                    </div>
                                    
                                    {/* Common Line Colors */}
                                    <div className="qa-brand-colors qa-brand-colors-small" style={{ marginTop: '4px' }}>
                                        {['#e0e0e0', '#d0d0d0', '#b0b0b0', '#808080'].map((commonColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className="qa-brand-color qa-brand-color-small"
                                                style={{ backgroundColor: commonColor }}
                                                onClick={() => {
                                                    onStyleChange({ qaSeparatorLineColor: commonColor });
                                                }}
                                                title={commonColor}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="qa-effect-divider" />
                        
                        {/* Item Border (nur wenn Separator Mode OFF) */}
                        {!separatorLineMode && (
                        <div className="qa-effect-group">
                            <div className="qa-effect-header">
                                <label className="btn-custom-toggle-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={itemBorderEnabled || false}
                                        onChange={(e) => onStyleChange({ qaItemBorderEnabled: e.target.checked })}
                                        className="btn-custom-toggle-input"
                                    />
                                    <span className="btn-custom-toggle-slider"></span>
                                    <span className="btn-custom-toggle-label">Border</span>
                                </label>
                            </div>
                            
                            {itemBorderEnabled && (
                                <div className="qa-effect-controls">
                                    <div className="typo-control">
                                        <label>Width</label>
                                        <input
                                            type="range"
                                            min="1"
                                            max="8"
                                            value={itemBorderWidth || 1}
                                            onChange={(e) => {
                                                onStyleChange({ qaItemBorderWidth: parseInt(e.target.value) });
                                            }}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">{itemBorderWidth || 1}px</span>
                                    </div>
                                    
                                    <span className="qa-color-label" style={{ fontSize: '10px', marginBottom: '4px' }}>Border Color</span>
                                    
                                    {/* Brand Colors für Border */}
                                    <div className="qa-brand-colors qa-brand-colors-small">
                                        {Object.values(colors).map((brandColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className="qa-brand-color qa-brand-color-small"
                                                style={{ backgroundColor: brandColor }}
                                                onClick={() => {
                                                    onStyleChange({ qaItemBorderColor: brandColor });
                                                }}
                                                title={`Item Border Color ${index + 1}`}
                                            />
                                        ))}
                                    </div>
                                    
                                    {/* Common Border Colors */}
                                    <div className="qa-brand-colors qa-brand-colors-small" style={{ marginTop: '4px' }}>
                                        {['#e0e0e0', '#d0d0d0', '#b0b0b0', '#808080'].map((commonColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className="qa-brand-color qa-brand-color-small"
                                                style={{ backgroundColor: commonColor }}
                                                onClick={() => {
                                                    onStyleChange({ qaItemBorderColor: commonColor });
                                                }}
                                                title={commonColor}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        )}
                        
                        {!separatorLineMode && <div className="qa-effect-divider" />}
                        
                        {/* Item Shadow (nur wenn Separator Mode OFF) */}
                        {!separatorLineMode && (
                        <div className="qa-effect-group">
                            <div className="qa-effect-header">
                                <label className="btn-custom-toggle-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={itemShadowEnabled || false}
                                        onChange={(e) => onStyleChange({ qaItemShadowEnabled: e.target.checked })}
                                        className="btn-custom-toggle-input"
                                    />
                                    <span className="btn-custom-toggle-slider"></span>
                                    <span className="btn-custom-toggle-label">Shadow</span>
                                </label>
                            </div>
                            
                            {itemShadowEnabled && (
                                <div className="qa-effect-controls">
                                    <div className="typo-control">
                                        <label>Blur</label>
                                        <input
                                            type="range"
                                            min="5"
                                            max="40"
                                            value={itemShadowIntensity || 8}
                                            onChange={(e) => {
                                                onStyleChange({ qaItemShadowIntensity: parseInt(e.target.value) });
                                            }}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">{itemShadowIntensity || 8}px</span>
                                    </div>
                                    
                                    <div className="typo-control">
                                        <label>Distance</label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="20"
                                            value={itemShadowDistance || 2}
                                            onChange={(e) => {
                                                onStyleChange({ qaItemShadowDistance: parseInt(e.target.value) });
                                            }}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">{itemShadowDistance || 2}px</span>
                                    </div>
                                    
                                    <div className="typo-control">
                                        <label>Opacity</label>
                                        <input
                                            type="range"
                                            min="5"
                                            max="60"
                                            value={itemShadowOpacity || 10}
                                            onChange={(e) => {
                                                onStyleChange({ qaItemShadowOpacity: parseInt(e.target.value) });
                                            }}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">{itemShadowOpacity || 10}%</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        )}
                        
                        <span className="qa-section-label">Q&A Item Styling</span>
                    </div>
                )}

                {/* 4️⃣ ICON SETTINGS */}
                {expandedSection === 'icon-settings' && (
                    <div className="qa-expanded-section">
                        {/* Icon Style */}
                        <div className="qa-icon-style-group">
                            <span className="qa-color-label">Icon Style</span>
                            <div className="qa-icon-style-grid">
                                {/* Row 1 - Rounded */}
                                <button
                                    className={`qa-icon-style-btn ${(!iconStyle || iconStyle === 'chevron') ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ qaIconStyle: 'chevron' })}
                                    title="Chevron (Slim)"
                                >
                                    <i className="fas fa-chevron-down"></i>
                                    <span>Chevron</span>
                                </button>
                                <button
                                    className={`qa-icon-style-btn ${iconStyle === 'angle' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ qaIconStyle: 'angle' })}
                                    title="Angle (Wide)"
                                >
                                    <i className="fas fa-angle-down"></i>
                                    <span>Angle</span>
                                </button>
                                <button
                                    className={`qa-icon-style-btn ${iconStyle === 'angle-double' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ qaIconStyle: 'angle-double' })}
                                    title="Double Angle (Sharp)"
                                >
                                    <i className="fas fa-angle-double-down"></i>
                                    <span>Double</span>
                                </button>
                                
                                {/* Row 2 - Sharp & Edgy */}
                                <button
                                    className={`qa-icon-style-btn qa-icon-sharp ${iconStyle === 'caret-sharp' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ qaIconStyle: 'caret-sharp' })}
                                    title="Caret Sharp (Triangle)"
                                >
                                    <i className="fas fa-caret-down"></i>
                                    <span>Sharp</span>
                                </button>
                                <button
                                    className={`qa-icon-style-btn qa-icon-thin ${iconStyle === 'long-arrow' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ qaIconStyle: 'long-arrow' })}
                                    title="Long Arrow (Elegant)"
                                >
                                    <i className="fas fa-long-arrow-alt-down"></i>
                                    <span>Long</span>
                                </button>
                                <button
                                    className={`qa-icon-style-btn qa-icon-thin ${iconStyle === 'arrow-thin' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ qaIconStyle: 'arrow-thin' })}
                                    title="Arrow Thin (Minimal)"
                                >
                                    <i className="fas fa-arrow-down"></i>
                                    <span>Thin</span>
                                </button>
                                
                                {/* Row 3 - Plus/Special */}
                                <button
                                    className={`qa-icon-style-btn ${iconStyle === 'plus-minus' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ qaIconStyle: 'plus-minus' })}
                                    title="Plus/Minus"
                                >
                                    <i className="fas fa-plus"></i>
                                    <span>Plus</span>
                                </button>
                                <button
                                    className={`qa-icon-style-btn ${iconStyle === 'circle-plus' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ qaIconStyle: 'circle-plus' })}
                                    title="Circle Plus/Minus"
                                >
                                    <i className="fas fa-plus-circle"></i>
                                    <span>Circle</span>
                                </button>
                                <button
                                    className={`qa-icon-style-btn qa-icon-sharp ${iconStyle === 'chevron-circle' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ qaIconStyle: 'chevron-circle' })}
                                    title="Chevron Circle (Elegant)"
                                >
                                    <i className="fas fa-chevron-circle-down"></i>
                                    <span>Elegant</span>
                                </button>
                            </div>
                        </div>
                        
                        <div className="qa-effect-divider" />
                        
                        {/* Icon Color */}
                        <div className="qa-color-group">
                            <span className="qa-color-label">Icon Color</span>
                            
                            {/* Brand Colors */}
                            <div className="qa-brand-colors">
                                {Object.values(colors).map((brandColor, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className="qa-brand-color"
                                        style={{ backgroundColor: brandColor }}
                                        onClick={() => {
                                            onStyleChange({ qaIconColor: brandColor });
                                        }}
                                        title={`Icon Color ${index + 1}`}
                                    />
                                ))}
                            </div>
                            
                            {/* Common Icon Colors */}
                            <div className="qa-brand-colors qa-brand-colors-small" style={{ marginTop: '6px' }}>
                                {['#333333', '#666666', '#999999', '#5C80BC'].map((commonColor, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className="qa-brand-color qa-brand-color-small"
                                        style={{ backgroundColor: commonColor }}
                                        onClick={() => {
                                            onStyleChange({ qaIconColor: commonColor });
                                        }}
                                        title={commonColor}
                                    />
                                ))}
                            </div>
                            
                            {/* Custom Color Slots für Icons */}
                            <div className="qa-custom-color-slots" style={{ marginTop: '4px' }}>
                                {customColorSlots.slice(0, 3).map((slotColor, slotIndex) => (
                                    <React.Fragment key={slotIndex}>
                                        <button
                                            type="button"
                                            className="qa-brand-color qa-color-slot qa-brand-color-small"
                                            style={{ 
                                                backgroundColor: slotColor,
                                                border: slotColor === '#ffffff' ? '2px dashed rgba(255, 255, 255, 0.3)' : '2px solid rgba(255, 255, 255, 0.3)'
                                            }}
                                            onClick={() => {
                                                if (slotColor !== '#ffffff') {
                                                    onStyleChange({ qaIconColor: slotColor });
                                                } else {
                                                    setEditingSlotIndex(slotIndex);
                                                    document.getElementById(`qa-icon-color-slot-${slotIndex}`).click();
                                                }
                                            }}
                                            onContextMenu={(e) => {
                                                e.preventDefault();
                                                setEditingSlotIndex(slotIndex);
                                                document.getElementById(`qa-icon-color-slot-${slotIndex}`).click();
                                            }}
                                            title={slotColor === '#ffffff' ? 'Click to set custom color' : `Custom: ${slotColor}`}
                                        >
                                            {slotColor === '#ffffff' && (
                                                <i className="fas fa-plus" style={{ fontSize: '8px', color: 'rgba(255, 255, 255, 0.5)' }}></i>
                                            )}
                                        </button>
                                        
                                        <input
                                            id={`qa-icon-color-slot-${slotIndex}`}
                                            type="color"
                                            value={slotColor}
                                            style={{ display: 'none' }}
                                            onChange={(e) => updateColorSlot(slotIndex, e.target.value)}
                                        />
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                        
                        <span className="qa-section-label">Icon Settings</span>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

export default QAFloatingToolbar;

